import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  // rAF-based scroll handler to avoid layout thrash and extra re-renders
  const scrollTick = useRef(false);
  const handleScroll = useCallback(() => {
    if (scrollTick.current) return;
    scrollTick.current = true;
    requestAnimationFrame(() => {
      const atTop = window.scrollY === 0;
      setShowNavbar((prev) => (prev !== atTop ? atTop : prev));
      scrollTick.current = false;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Lock body scroll when menu is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = menuOpen ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [menuOpen]);

  // Back button closes menu
  useEffect(() => {
    if (!menuOpen) return;
    window.history.pushState({ menuOpen: true }, "");
    const handlePopState = () => setMenuOpen(false);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [menuOpen]);

  // ESC to close menu, basic focus trap for drawer
  const drawerRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
      if (e.key === "Tab" && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    // Move focus into drawer
    setTimeout(() => {
      const firstLink = drawerRef.current?.querySelector("a,button");
      firstLink?.focus();
    }, 0);

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);
  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    closeMenu();
  };

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Violetblock", path: "/VioletBlock" },
    { name: "Blog", path: "/Blog" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <>
      {/* Skip to content for accessibility/SEO */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded"
      >
        Skip to content
      </a>

      {/* Header landmark wraps nav for semantics */}
      <header>
        {/* Navbar - Edge to edge */}
        <nav
          role="navigation"
          aria-label="Main navigation"
          className={`fixed top-0 left-0 w-full z-50 transition-transform duration-500 ${
            showNavbar ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          {/* Edge-to-edge background container */}
          <div className="w-full">
            {/* Content container with edge-to-edge padding on mobile */}
            <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3 w-full">
              {/* Logo + School Name */}
              <Link
                to="/"
                onClick={handleNavClick}
                className="flex items-center space-x-2 rtl:space-x-reverse min-w-0"
                aria-label="Go to home page"
                rel="prefetch"
              >
                <img
                  src="https://res.cloudinary.com/ddvpczzng/image/upload/v1757446087/emblem_cqwlrr.png"
                  alt="STVMHSS School Emblem"
                  loading="lazy"
                  decoding="async"
                  className="h-8 sm:h-10 md:h-12 w-auto object-contain flex-shrink-0"
                  width="40"
                  height="40"
                />
                <span className="hidden sm:inline text-sm sm:text-base md:text-lg font-serif text-white truncate [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)]">
                  St.Thomas Vidyalayam Matriculation Higher Secondary School
                </span>
                <span className="sm:hidden text-sm font-serif text-white [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)]">
                  STVMHSS
                </span>
              </Link>

              {/* Mobile Hamburger */}
              <div className="lg:hidden ml-4">
                <button
                  onClick={toggleMenu}
                  type="button"
                  className="inline-flex items-center justify-center p-2 rounded-md text-white border border-white/30 hover:bg-white/10 hover:border-white focus:outline-none transition [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)]"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-menu"
                  aria-label="Toggle navigation menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    role="img"
                    aria-hidden="true"
                    focusable="false"
                  >
                    {menuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>

              {/* Desktop Nav */}
              <div className="hidden lg:flex flex-1 justify-end">
                <ul className="font-serif flex space-x-6 xl:space-x-8">
                  {navItems.map((item) => (
                    <li className="relative group" key={item.path}>
                      <Link
                        to={item.path}
                        onClick={handleNavClick}
                        className={`block py-2 px-1 transition-colors duration-300 text-sm md:text-base [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)] ${
                          isActive(item.path)
                            ? "text-[#f5f5dc] font-medium"
                            : "text-white hover:text-[#f5f5dc]"
                        }`}
                        aria-current={isActive(item.path) ? "page" : undefined}
                        rel="prefetch"
                      >
                        {item.name}
                        <span
                          className={`absolute left-0 bottom-0 h-0.5 bg-[#00BCD4] transition-all duration-300 ${
                            isActive(item.path)
                              ? "w-full"
                              : "w-0 group-hover:w-full"
                          }`}
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Drawer */}
      <aside
        id="mobile-menu"
        ref={drawerRef}
        className={`fixed top-0 right-0 h-screen w-72 sm:w-80 bg-cyan-900/90 backdrop-blur-lg border-l border-white/10 shadow-2xl z-50 transform transition-transform duration-500 ease-in-out ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Mobile menu"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-white/10">
          <h2 className="text-[#f5f5dc] font-serif text-xl [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)]">
            Menu
          </h2>
          <button
            onClick={toggleMenu}
            className="text-[#f5f5dc] hover:text-white focus:outline-none transition [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)]"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              role="img"
              aria-hidden="true"
              focusable="false"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <ul className="flex flex-col font-serif py-6 space-y-2 px-2">
          {navItems.map((item, idx) => (
            <li
              key={item.path}
              className={`relative group transition duration-300 ease-out delay-${
                idx * 75
              }`}
            >
              <Link
                to={item.path}
                onClick={handleNavClick}
                className={`block w-full py-4 px-6 rounded-md text-base tracking-wide font-medium transition-all duration-300 ease-in-out [text-shadow:_0_1px_1px_rgb(0_0_0_/_80%)] ${
                  isActive(item.path)
                    ? "text-[#f5f5dc] bg-cyan-600"
                    : "text-white hover:text-[#f5f5dc] hover:bg-cyan-800/60"
                }`}
                aria-current={isActive(item.path) ? "page" : undefined}
                rel="prefetch"
              >
                {item.name}
                <span
                  className={`absolute left-0 bottom-0 h-0.5 bg-[#f5f5dc] transition-all duration-300 ${
                    isActive(item.path) ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main content anchor for skip link target */}
      <div id="main-content" className="sr-only" aria-hidden="true" />
    </>
  );
};

export default Navbar;
